import type { InputValues } from "components/Omnisearch/FilterPopover";

import { SubmissionMaterializationType } from "generated-graphql/graphql";
import {
  isRealSubmission,
  partitionSubmissions as partitionSubmissionsGeneric,
  type PartitionedSubmissions as GenericPartitionedSubmissions,
} from "utils/partitionSubmissions";

import { Row } from "./columns";

export type PartitionedSubmissions = GenericPartitionedSubmissions<Row>;

export function partitionSubmissions(submissions: Row[]): PartitionedSubmissions {
  return partitionSubmissionsGeneric(submissions, {
    getFrequency: (row) => row.taskFrequency,
    getSubmissionId: (row) => row.submissionId,
  });
}

/**
 * Formats a virtual event for display in a data grid
 */
export function formatVirtualEventForGrid(virtualEvent: Row) {
  return {
    id: virtualEvent.id,
    isVirtual: true,
    submission: {
      dueDate: virtualEvent.deadline,
      title: virtualEvent.title,
      submissionType: virtualEvent.submissionType,
      status: null,
    },
    facilities: virtualEvent.facilities,
  };
}

/**
 * Check if the row is a truly materialized submission (vs a virtual event or template).
 *
 * A row is truly materialized only if it has task kind INSTANCE or STANDALONE
 * (created by materializing a virtual event or as a one-off).
 *
 * Important: Template submissions for recurring series have kind TEMPLATE
 * and should NOT be considered materialized even if their status was changed from NOT_STARTED.
 *
 * @deprecated Use getMaterializationType for more precise classification
 */
export function isMaterialized(row: Row): boolean {
  return isRealSubmission(row, (r) => r.taskFrequency);
}

/**
 * Get the materialization type of a row. This distinguishes between:
 * - VIRTUAL_EVENT: From a recurring series, not yet materialized
 * - MATERIALIZED: ONCE task that was materialized from a recurring series (has recurring parent)
 * - ONE_OFF: ONCE task created directly with no recurring parent
 *
 * The materializationType field is computed by the backend based on whether the
 * series has a recurring task.
 */
export function getMaterializationType(row: Row): SubmissionMaterializationType {
  return row.materializationType;
}

/**
 * Check if a row represents a materialized instance that can be reverted to a virtual event.
 * Only truly materialized instances (with a recurring parent) can be deleted and returned to virtual state.
 */
export function canRevertToVirtualEvent(row: Row): boolean {
  return (
    row.materializationType === SubmissionMaterializationType.Materialized &&
    row.submissionId != null
  );
}

type OmnisearchParser = (omnisearch: string) => InputValues;
type OmnisearchStringifier = (inputValues: InputValues) => string;

/**
 * Merge default key:value filters into the current omnisearch string while preserving
 * any raw free-text tokens in the user's search.
 */
export function mergeOmnisearchWithDefaultsPreservingFreeText({
  omnisearch,
  defaultSearch,
  parseOmnisearch,
  stringifyOmnisearch,
}: {
  omnisearch: string | null | undefined;
  defaultSearch: string | null | undefined;
  parseOmnisearch: OmnisearchParser;
  stringifyOmnisearch: OmnisearchStringifier;
}): string | null {
  const trimmedOmnisearch = omnisearch?.trim() ?? "";
  const trimmedDefaultSearch = defaultSearch?.trim() ?? "";

  if (!trimmedOmnisearch) {
    return trimmedDefaultSearch || null;
  }

  if (!trimmedDefaultSearch) {
    return trimmedOmnisearch;
  }

  const userFilters = parseOmnisearch(trimmedOmnisearch);
  const defaultFilters = parseOmnisearch(trimmedDefaultSearch);

  const missingDefaultFilters: InputValues = {};
  for (const [key, value] of Object.entries(defaultFilters)) {
    if (!(key in userFilters)) {
      missingDefaultFilters[key] = value;
    }
  }

  const missingDefaultsSearch = stringifyOmnisearch(missingDefaultFilters).trim();
  if (!missingDefaultsSearch) {
    return trimmedOmnisearch;
  }

  return `${trimmedOmnisearch} ${missingDefaultsSearch}`.trim();
}
